/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable func-names */

import db from 'modules/localForage/db';
import { getArrayBufferFromLocalForage } from 'modules/localForage/utils';
import { emptyGeojson } from 'modules/mapLayers/consts';
import { makeEmptyGeojson } from 'modules/geojson/utils';
import { addToResolveMap, getWorker } from './workers';
import { Constraint } from '../types';
import { getLatLngSteps, getWindMetadataFromExif } from './metadata';
import { getWindValueGetters, getWindValues } from './pixelToData';
import { MeteoDataTypes } from '../consts';

const getImageCanvas = async (
  width: number,
  height: number,
  url: string,
): Promise<HTMLCanvasElement> => {
  const image = new Image(width, height);
  return new Promise((resolve) => {
    image.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      if (!canvas.getContext('2d')) {
        throw new Error('No canvas context');
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      canvas.getContext('2d').drawImage(this, 0, 0, width, height);
      resolve(canvas);
    };
    image.src = url;
  });
};

export const getWindComparisonDataFromJpegs = async (
  ecmwfStorageKey: string,
  gfsStorageKey: string,
  options?: {
    constraints: Constraint[];
  },
): Promise<{
  fillGeoJson1: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  barbGeoJson1: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  fillGeoJson2: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  barbGeoJson2: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
}> => {
  try {
    const [ecmwfFileBuffer, gfsFileBuffer] = await Promise.all([
      getArrayBufferFromLocalForage(db.meteo, ecmwfStorageKey),
      getArrayBufferFromLocalForage(db.meteo, gfsStorageKey),
    ]);
    const constraints = options?.constraints;
    if (!ecmwfFileBuffer || !gfsFileBuffer) throw new Error('No file buffer');
    const ecmwfBlob = new Blob([ecmwfFileBuffer], { type: 'image/jpeg' });
    const gfsBlob = new Blob([gfsFileBuffer], { type: 'image/jpeg' });
    const ecmwfUrl = URL.createObjectURL(ecmwfBlob);
    const gfsUrl = URL.createObjectURL(gfsBlob);
    const ecmwfMeta = getWindMetadataFromExif(ecmwfFileBuffer);
    const gfsMeta = getWindMetadataFromExif(gfsFileBuffer);
    const ecmwfCanvas = await getImageCanvas(
      ecmwfMeta.width,
      ecmwfMeta.height,
      ecmwfUrl,
    );
    const gfsCanvas = await getImageCanvas(
      gfsMeta.width,
      gfsMeta.height,
      gfsUrl,
    );
    const fillGeoJson1 = makeEmptyGeojson();
    const barbGeoJson1 = makeEmptyGeojson();
    const fillGeoJson2 = makeEmptyGeojson();
    const barbGeoJson2 = makeEmptyGeojson();
    const storageKey = `${ecmwfStorageKey}-${gfsStorageKey}`;
    return new Promise((resolve) => {
      addToResolveMap(storageKey, {
        fillGeoJson1,
        barbGeoJson1,
        fillGeoJson2,
        barbGeoJson2,
        count: 0,
        resolve,
      });
      for (let i = 0; i < ecmwfMeta.height; i += 1) {
        const pixelArray: Array<Uint8ClampedArray[]> = [];
        for (let j = 0; j < ecmwfMeta.width; j += 1) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const ecmwfPixelData = ecmwfCanvas
            .getContext('2d')
            .getImageData(j, ecmwfMeta.height - (i + 1), 1, 1).data;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const gfsPixelData = gfsCanvas
            .getContext('2d')
            .getImageData(j, ecmwfMeta.height - (i + 1), 1, 1).data;
          pixelArray.push([ecmwfPixelData, gfsPixelData]);
        }
        getWorker(i).postMessage({
          array: pixelArray,
          metadata: [ecmwfMeta, gfsMeta],
          i,
          constraints,
          storageKey,
          size: ecmwfMeta.height,
          type: MeteoDataTypes.WindComparison,
        });
      }
    });
  } catch {
    return {
      fillGeoJson1: emptyGeojson,
      barbGeoJson1: emptyGeojson,
      fillGeoJson2: emptyGeojson,
      barbGeoJson2: emptyGeojson,
    };
  }
};

export const getWindDataFromJpeg = async (
  storageKey: string,
  options?: {
    constraints: Constraint[];
  },
): Promise<{
  fillGeoJson1: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  barbGeoJson1: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  fillGeoJson2: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
  barbGeoJson2: GeoJSON.FeatureCollection<GeoJSON.Geometry>;
}> => {
  try {
    const fileBuffer = await getArrayBufferFromLocalForage(
      db.meteo,
      storageKey,
    );
    if (!fileBuffer) throw new Error('No file buffer');
    const constraints = options?.constraints;
    const blob = new Blob([fileBuffer], { type: 'image/jpeg' });
    const url = URL.createObjectURL(blob);
    const metadata = getWindMetadataFromExif(fileBuffer);
    const { height, width } = metadata;
    const image = new Image(width, height);
    return new Promise((resolve) => {
      image.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        if (!canvas.getContext('2d')) {
          throw new Error('No canvas context');
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        canvas.getContext('2d').drawImage(this, 0, 0, width, height);
        const fillGeoJson1 = makeEmptyGeojson();
        const barbGeoJson1 = makeEmptyGeojson();
        const fillGeoJson2 = makeEmptyGeojson();
        const barbGeoJson2 = makeEmptyGeojson();
        addToResolveMap(storageKey, {
          fillGeoJson1,
          barbGeoJson1,
          fillGeoJson2,
          barbGeoJson2,
          count: 0,
          resolve,
        });
        for (let i = 0; i < height; i += 1) {
          const pixelArray: Uint8ClampedArray[] = [];
          for (let j = 0; j < width; j += 1) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const pixelData = canvas
              .getContext('2d')
              .getImageData(j, height - (i + 1), 1, 1).data;
            pixelArray.push(pixelData);
          }
          getWorker(i).postMessage({
            array: pixelArray,
            metadata,
            i,
            constraints,
            storageKey,
            size: height,
            type: MeteoDataTypes.Wind,
          });
        }
      };
      image.src = url;
    });
  } catch {
    return {
      fillGeoJson1: emptyGeojson,
      barbGeoJson1: emptyGeojson,
      fillGeoJson2: emptyGeojson,
      barbGeoJson2: emptyGeojson,
    };
  }
};

export const getWindDataFromPixel = async (
  storageKey: string,
  options?: {
    constraints?: Constraint[];
    point?: number[];
  },
): Promise<any> => {
  try {
    const fileBuffer = await getArrayBufferFromLocalForage(
      db.meteo,
      storageKey,
    );
    // const hasConstrains = !!options?.constraints?.length;
    // const constraints = options?.constraints;
    if (!fileBuffer) throw new Error('No file buffer');
    const blob = new Blob([fileBuffer], { type: 'image/jpeg' });
    const url = URL.createObjectURL(blob);
    const metadata = getWindMetadataFromExif(fileBuffer);
    const getters = getWindValueGetters(metadata);
    const { lngStep, latStep } = getLatLngSteps(metadata);
    const { width, height, minLat, minLng } = metadata;
    const image = new Image(width, height);
    return new Promise((resolve) => {
      image.onload = function () {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        if (!canvas.getContext('2d')) {
          throw new Error('No canvas context');
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        canvas.getContext('2d').drawImage(this, 0, 0, width, height);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const [lat, lng] = options?.point;

        const pixelLat = Math.floor((lat - minLat) / latStep);
        const pixelLng = Math.floor((lng - minLng) / lngStep);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const pixelData = canvas
          .getContext('2d')
          .getImageData(pixelLng, height - (pixelLat + 1), 1, 1).data;

        const values = getWindValues(getters, pixelData);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        resolve(values);
      };
      image.src = url;
    });
  } catch {
    return {};
  }
};
